import { useState, useMemo } from 'react';
import { FileText, Plus, Pencil, Trash2 } from 'lucide-react';
import { palette, transition } from '../../styles/tokens';
import { EmptyState } from '../../components/common/EmptyState';
import { SlidePanel } from '../../components/common/SlidePanel';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { TH, TD, LABEL, INPUT } from './config';
import { useJobTemplatesAdmin, type AdminSavedTemplate } from './templateHooks';
import { getJobTemplatesForIndustry, type JobTemplate, type JobTemplateLineItem } from '../../data/jobTemplates';

// ── Types ──

interface Props {
  businessId: string;
  industryId: string;
  userId: string;
}

// ── Helpers ──

function fmtDuration(min: number): string {
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA');
}

// ── Edit Panel ──

function EditTemplatePanel({ isOpen, onClose, template, onSave, onDelete }: {
  isOpen: boolean;
  onClose: () => void;
  template: AdminSavedTemplate | null;
  onSave: (data: Partial<AdminSavedTemplate>) => Promise<boolean>;
  onDelete?: (id: string) => Promise<boolean>;
}) {
  const isEdit = !!template?.id;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState('60');
  const [priority, setPriority] = useState('medium');
  const [tags, setTags] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Reset on open
  useState(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description ?? '');
      setDuration(String(template.estimated_minutes));
      setPriority(template.priority);
      setTags((template.tags ?? []).join(', '));
    } else {
      setName(''); setDescription(''); setDuration('60'); setPriority('medium'); setTags('');
    }
  });

  // Re-sync when template changes
  if (isOpen && template && name === '' && template.name) {
    setName(template.name);
    setDescription(template.description ?? '');
    setDuration(String(template.estimated_minutes));
    setPriority(template.priority);
    setTags((template.tags ?? []).join(', '));
  }

  const handleSubmit = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError(null);
    const ok = await onSave({
      name: name.trim(),
      description: description.trim() || null,
      estimated_minutes: parseInt(duration) || 60,
      priority,
      tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      checklist: template?.checklist ?? [],
      line_items: template?.line_items ?? [],
      source_template_id: template?.source_template_id ?? null,
    });
    setSaving(false);
    if (ok) onClose();
    else setError('Failed to save template');
  };

  const F: React.CSSProperties = { marginBottom: 14 };

  return (
    <SlidePanel isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit Template' : 'New Template'} subtitle="Job template configuration">
      {error && <div style={{ padding: '8px 12px', borderRadius: 6, background: palette.redWash, color: palette.red, fontSize: 12.5, marginBottom: 16 }}>{error}</div>}

      <div style={F}><label style={LABEL}>Name *</label><input style={INPUT} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Clogged Drain" /></div>
      <div style={F}><label style={LABEL}>Description</label><textarea style={{ ...INPUT, minHeight: 60, resize: 'vertical' }} value={description} onChange={e => setDescription(e.target.value)} /></div>
      <div style={{ display: 'flex', gap: 12, ...F }}>
        <div style={{ flex: 1 }}><label style={LABEL}>Duration (minutes)</label><input type="number" style={INPUT} value={duration} onChange={e => setDuration(e.target.value)} /></div>
        <div style={{ flex: 1 }}><label style={LABEL}>Priority</label>
          <select style={INPUT} value={priority} onChange={e => setPriority(e.target.value)}>
            <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option>
          </select>
        </div>
      </div>
      <div style={F}><label style={LABEL}>Tags (comma-separated)</label><input style={INPUT} value={tags} onChange={e => setTags(e.target.value)} placeholder="drain, clog, blocked, sink" /></div>

      {/* Line items preview */}
      {template?.line_items && template.line_items.length > 0 && (
        <div style={F}>
          <label style={LABEL}>Line Items ({template.line_items.length})</label>
          <div style={{ border: `1px solid ${palette.g200}`, borderRadius: 8, overflow: 'hidden' }}>
            {template.line_items.map((li: JobTemplateLineItem, i: number) => (
              <div key={i} style={{ padding: '8px 12px', borderBottom: i < template.line_items.length - 1 ? `1px solid ${palette.g100}` : 'none', display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: palette.g700 }}>{li.description}</span>
                <span style={{ color: palette.g500 }}>${li.defaultAmount}{li.unit ? `/${li.unit}` : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Checklist preview */}
      {template?.checklist && template.checklist.length > 0 && (
        <div style={F}>
          <label style={LABEL}>Checklist ({template.checklist.length} items)</label>
          <div style={{ border: `1px solid ${palette.g200}`, borderRadius: 8, overflow: 'hidden' }}>
            {template.checklist.map((c: { id: string; label: string }, i: number) => (
              <div key={c.id} style={{ padding: '6px 12px', borderBottom: i < template.checklist.length - 1 ? `1px solid ${palette.g100}` : 'none', fontSize: 13, color: palette.g700 }}>
                {c.label}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
        <div>
          {isEdit && onDelete && (
            <button onClick={() => setConfirmDelete(true)} style={{
              padding: '8px 16px', borderRadius: 8, border: `1px solid ${palette.redWash}`,
              background: palette.white, fontSize: 13, fontWeight: 500, color: palette.red,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>Delete</button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: saving ? palette.g300 : palette.accent, color: palette.white, fontSize: 13.5, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>{saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}</button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmDelete}
        title={`Delete "${template?.name ?? ''}"?`}
        message="This template will be permanently removed. Existing jobs created from it are unaffected."
        confirmLabel="Delete template"
        confirmVariant="danger"
        loading={deleting}
        onClose={() => { if (!deleting) setConfirmDelete(false); }}
        onConfirm={async () => {
          if (!template?.id || !onDelete) return;
          setDeleting(true);
          try {
            await onDelete(template.id);
            setConfirmDelete(false);
            onClose();
          } finally { setDeleting(false); }
        }}
      />
    </SlidePanel>
  );
}

// ── Main Tab ──

export function TemplatesTab({ businessId, industryId, userId }: Props) {
  const { templates, createTemplate, updateTemplate, deleteTemplate } = useJobTemplatesAdmin(businessId, userId);
  const defaults = useMemo(() => getJobTemplatesForIndustry(industryId), [industryId]);
  const [search, setSearch] = useState('');
  const [showPanel, setShowPanel] = useState(false);
  const [editing, setEditing] = useState<AdminSavedTemplate | null>(null);
  const [rowDelete, setRowDelete] = useState<AdminSavedTemplate | null>(null);
  const [rowDeleting, setRowDeleting] = useState(false);

  const customizedIds = useMemo(() => new Set(templates.filter(t => t.source_template_id).map(t => t.source_template_id!)), [templates]);

  const filteredSaved = useMemo(() => {
    if (!search) return templates;
    const q = search.toLowerCase();
    return templates.filter(t => t.name.toLowerCase().includes(q) || (t.tags ?? []).some(tag => tag.toLowerCase().includes(q)));
  }, [templates, search]);

  const filteredDefaults = useMemo(() => {
    const remaining = defaults.filter(d => !customizedIds.has(d.id));
    if (!search) return remaining;
    const q = search.toLowerCase();
    return remaining.filter(d => d.name.toLowerCase().includes(q) || d.tags.some(tag => tag.toLowerCase().includes(q)));
  }, [defaults, customizedIds, search]);

  const handleEditSaved = (tpl: AdminSavedTemplate) => {
    setEditing(tpl);
    setShowPanel(true);
  };

  const handleEditDefault = (tpl: JobTemplate) => {
    // Pre-fill a new template from default
    setEditing({
      id: '',
      business_id: businessId,
      name: tpl.name,
      description: tpl.description,
      estimated_minutes: tpl.estimatedMinutes,
      priority: tpl.priority,
      checklist: tpl.checklist.map((label, i) => ({ id: String(i), label })),
      line_items: tpl.lineItems,
      tags: tpl.tags,
      source_template_id: tpl.id,
      use_count: 0,
      is_active: true,
      created_at: '',
      updated_at: '',
    });
    setShowPanel(true);
  };

  const handleCreate = () => {
    setEditing(null);
    setShowPanel(true);
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', justifyContent: 'space-between' }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search templates..." style={{ padding: '7px 12px', borderRadius: 6, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: 240 }} />
        <button onClick={handleCreate} style={{
          display: 'flex', alignItems: 'center', gap: 4, padding: '7px 14px', borderRadius: 8,
          border: 'none', background: palette.accent, fontSize: 13, fontWeight: 600,
          color: palette.white, cursor: 'pointer', fontFamily: 'inherit',
        }}><Plus size={15} /> New Template</button>
      </div>

      {/* Saved Templates */}
      {filteredSaved.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 600, color: palette.g400, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            My Templates ({filteredSaved.length})
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
            <thead><tr>
              <th style={TH}>Name</th>
              <th style={TH}>Duration</th>
              <th style={TH}>Priority</th>
              <th style={{ ...TH, textAlign: 'right' }}>Use Count</th>
              <th style={TH}>Last Used</th>
              <th style={TH}>Actions</th>
            </tr></thead>
            <tbody>
              {filteredSaved.map(tpl => (
                <tr key={tpl.id}
                  style={{ cursor: 'pointer', transition: `background ${transition.fast}` }}
                  onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  onClick={() => handleEditSaved(tpl)}>
                  <td style={{ ...TD, fontWeight: 600, color: palette.g800 }}>
                    {tpl.name}
                    {tpl.source_template_id && (
                      <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 6px', borderRadius: 4, background: palette.greenWash, color: palette.green, fontWeight: 500 }}>Customised</span>
                    )}
                  </td>
                  <td style={TD}>{fmtDuration(tpl.estimated_minutes)}</td>
                  <td style={TD}><span style={{ textTransform: 'capitalize' }}>{tpl.priority}</span></td>
                  <td style={{ ...TD, textAlign: 'right' }}>{tpl.use_count}</td>
                  <td style={TD}>{tpl.updated_at ? fmtDate(tpl.updated_at) : '—'}</td>
                  <td style={TD}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={e => { e.stopPropagation(); handleEditSaved(tpl); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: palette.g400, padding: 4 }}><Pencil size={14} /></button>
                      <button onClick={e => { e.stopPropagation(); setRowDelete(tpl); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: palette.red, padding: 4 }}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* Default Templates */}
      {filteredDefaults.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 600, color: palette.g400, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            Default Templates ({filteredDefaults.length})
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={TH}>Name</th>
              <th style={TH}>Duration</th>
              <th style={TH}>Priority</th>
              <th style={TH}>Items</th>
              <th style={TH}>Checklist</th>
              <th style={TH}>Action</th>
            </tr></thead>
            <tbody>
              {filteredDefaults.map(tpl => (
                <tr key={tpl.id}
                  style={{ cursor: 'pointer', transition: `background ${transition.fast}` }}
                  onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  onClick={() => handleEditDefault(tpl)}>
                  <td style={{ ...TD, fontWeight: 500, color: palette.g700 }}>
                    {tpl.name}
                    <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 6px', borderRadius: 4, background: palette.g100, color: palette.g500, fontWeight: 500 }}>Default</span>
                  </td>
                  <td style={TD}>{fmtDuration(tpl.estimatedMinutes)}</td>
                  <td style={TD}><span style={{ textTransform: 'capitalize' }}>{tpl.priority}</span></td>
                  <td style={TD}>{tpl.lineItems.length}</td>
                  <td style={TD}>{tpl.checklist.length}</td>
                  <td style={TD}>
                    <button onClick={e => { e.stopPropagation(); handleEditDefault(tpl); }} style={{
                      padding: '3px 10px', borderRadius: 4, border: `1px solid ${palette.g200}`,
                      background: palette.white, fontSize: 11, fontWeight: 500, color: palette.g600,
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}>Customise</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* Empty state */}
      {filteredSaved.length === 0 && filteredDefaults.length === 0 && (
        <EmptyState icon={<FileText size={22} />} title="No templates found" subtitle={search ? 'Try a different search.' : 'Create a template to get started.'} actionLabel="New Template" onAction={handleCreate} />
      )}

      {/* Edit Panel */}
      <EditTemplatePanel
        isOpen={showPanel}
        onClose={() => { setShowPanel(false); setEditing(null); }}
        template={editing}
        onSave={async (data) => {
          if (editing?.id) {
            return updateTemplate(editing.id, data);
          }
          return createTemplate(data);
        }}
        onDelete={editing?.id ? (id) => deleteTemplate(id) : undefined}
      />

      <ConfirmDialog
        isOpen={!!rowDelete}
        title={`Delete "${rowDelete?.name ?? ''}"?`}
        message="This template will be permanently removed. Existing jobs created from it are unaffected."
        confirmLabel="Delete template"
        confirmVariant="danger"
        loading={rowDeleting}
        onClose={() => { if (!rowDeleting) setRowDelete(null); }}
        onConfirm={async () => {
          if (!rowDelete) return;
          setRowDeleting(true);
          try {
            await deleteTemplate(rowDelete.id);
            setRowDelete(null);
          } finally { setRowDeleting(false); }
        }}
      />
    </div>
  );
}
